import { getText } from '../utils/text';

/**
 * The query service nested grouping API may round aggregated event counts due to performance or
 * memory limitations. This utility rounds and abbreviates the results to display a safe level of
 * precision for the user.
 *
 * < 1,000: Display the number as-is
 * 1,000 - 9,999: Round to the nearest 100, display in thousands (e.g. 3.2K)
 * 10,000 - 99,999: Round to the nearest 500, display in thousands (e.g. 32.5K)
 * 100,000 - 999,500: Round to the nearest 1,000, display in thousands (e.g. 132K)
 * 999,500+: Round to the nearest 10,000, display in millions (e.g. 1.32M)
 *
 * @param results The original number of results from the server
 * @returns A rounded and abbreviated display string
 */
export const formatImpreciseCount = (results: number): string => {
  if (results < 1_000) {
    return results.toString();
  }
  if (results < 10_000) {
    // round to the nearest 100, display in thousands (e.g. 3.2K)
    const rounded = (Math.round(results / 100) * 100) / 1_000;
    return getText('thousands_abbreviation', { num: rounded });
  }
  if (results < 100_000) {
    // round to the nearest 500, display in thousands (e.g. 32.5K)
    const rounded = (Math.round(results / 500) * 500) / 1_000;
    return getText('thousands_abbreviation', { num: rounded });
  }
  if (results < 999_500) {
    // round to the nearest 1000, display in thousands (e.g. 132K)
    const rounded = Math.round(results / 1_000);
    return getText('thousands_abbreviation', { num: rounded });
  }
  // round to the nearest 10000, display in millions (e.g. 1.32M)
  const rounded = (Math.round(results / 10_000) * 10_000) / 1_000_000;
  return getText('millions_abbreviation', { num: rounded });
};
